import React, { Component, Fragment, createRef } from "react";
import PropTypes from "prop-types";
import { JOBS_SUB_MENU } from "@constants";
import { JobEventDraftsService } from "@services";
import utils from "@utils";
import { Link } from "react-router";
import { ConfirmationModal, ScrollDetect, TabMenu } from "../../../components";

import "./JobDrafts.scss";

class JobDrafts extends Component {
	constructor(props) {
		super(props);
		this.state = {
			data: [],
			pageNumber: 1,
			hasMore: true,
		};

		this._confirmationModal = createRef();
	}

	/** Retrieves a paginated list of job drafts for the current user */
	getJobDrafts = () => {
		const { lang } = global;
		const { pageNumber } = this.state;
		JobEventDraftsService.getJobDrafts(pageNumber)
			.then(({ data }) => {
				const newData = data.ItemList || [];
				this.setState(prevState => ({
					data: [...prevState.data, ...newData],
					pageNumber: prevState.pageNumber + 1,
					hasMore: newData.length >= data.DisplayCount,
				}));
			})
			.catch(() => {
				this.setState({ data: [], pageNumber: 1, hasMore: false });
				utils.notify({
					type: "error",
					message: lang.t("Error loading job drafts"),
					timeOut: 1500,
				});
			});
	};

	/**
	 * Confirms the user's intention to delete a job draft and updates the UI after completion
	 * @param {Number} draftId The ID number for the job draft
	 * @param {String} draftTitle The name of the job draft
	 * @returns {(event:Event) => void}
	 */
	deleteDraft = (draftId, draftTitle) => {
		const { lang } = global;
		this._confirmationModal.current.openModal({
			title: lang.t("Delete Draft"),
			message: `${lang.t("Are you sure you want to delete this draft for")} ${draftTitle}?`,
			confirmButton: lang.t("Yes"),
			onConfirm: () => {
				this.setState(prevState => ({
					data: prevState.data.map(item =>
						item.Id === draftId ? { ...item, itemLoading: true } : item,
					),
				}));
				JobEventDraftsService.deleteJobDraft(draftId)
					.then(() => {
						this.setState(prevState => ({
							data: prevState.data.filter(item => item.Id !== draftId),
						}));
						utils.notify({
							type: "success",
							message: lang.t("Job draft deleted"),
							timeOut: 1500,
						});
					})
					.catch(() => {
						this.setState(prevState => ({
							data: prevState.data.map(item =>
								item.Id === draftId ? { ...item, itemLoading: false } : item,
							),
						}));
						utils.notify({
							type: "error",
							message: lang.t("Unable to delete job draft"),
							timeOut: 1500,
						});
					});
			},
		});
	};

	onClickTab = ({ tabNumber, url }) => {
		const {
			router: { push },
		} = this.props;
		const thisTab = JOBS_SUB_MENU.find(tab => tab.type === "drafts");
		if (tabNumber !== thisTab.tabNumber) push(url);
	};

	render() {
		const { lang } = global;
		const { data, hasMore } = this.state;
		const { user } = this.props;

		return (
			<Fragment>
				<TabMenu
					tabs={JOBS_SUB_MENU}
					activeTab={JOBS_SUB_MENU.find(tab => tab.type === "drafts").tabNumber}
					onClickTab={this.onClickTab}
					user={user}
				/>
				<div
					id="jobdraft"
					data-test-id="jobs__jobdraft"
					className="container"
				>
					<div className="row">
						<div className="col-sm-12">
							<h2>Job Drafts</h2>
						</div>
					</div>
					<div className="row hidden-xs list-header">
						<div className="col-sm-3">
							<strong>{lang.t("Title")}</strong>
						</div>
						<div className="col-sm-3">
							<strong>{lang.t("Created")}</strong>
						</div>
						<div className="col-sm-3">
							<strong>{lang.t("Updated")}</strong>
						</div>
					</div>
					<div className="list-body">
						<Choose>
							<When condition={data.length > 0}>
								<For
									each="item"
									of={data}
								>
									<Link
										key={`job_draft_${item.Id}`}
										to={`/jobs/add?draftId=${item.Id}`}
									>
										<div className="row card jobdraft-row v-align-sm">
											<div
												className="col-sm-3"
												title={item.Title}
											>
												{item.Title}
												<br className="visible-xs" />
											</div>
											<div
												className="col-sm-3"
												title={`${item.UserFirstName} ${item.UserLastName}`}
											>
												<span className="visible-sm">Created&nbsp;</span>
												{utils.formatDate(
													`${item.CreatedTime}Z`,
													"en-US",
													user.TimeZoneId,
													user.UserPreferences === "useMilitaryTime"
														? "dt-num-mil"
														: "dt-num",
												)}
												<br className="visible-xs" />
											</div>
											<div className="col-sm-3">
												<If condition={item.UpdatedTime}>
													<span className="visible-sm">
														Updated&nbsp;
													</span>
													{utils.formatDate(
														`${item.UpdatedTime}Z`,
														"en-US",
														user.TimeZoneId,
														user.UserPreferences === "useMilitaryTime"
															? "dt-num-mil"
															: "dt-num",
													)}
												</If>
											</div>
											<div className="col-sm-2 col-sm-offset-1 text-right">
												<br className="visible-xs" />
												<button
													type="button"
													aria-label="Delete job draft"
													onClick={e => {
														e.preventDefault();
														this.deleteDraft(item.Id, item.Title);
													}}
													data-prevent-navigation
													className="btn btn-primary btn-block"
												>
													{lang.t("Delete")}
												</button>
											</div>
										</div>
									</Link>
								</For>
							</When>
							<Otherwise>
								<div className="spacing-top text-center text-muted">
									{lang.t("No job drafts found")}
								</div>
							</Otherwise>
						</Choose>
						<If condition={hasMore}>
							<ScrollDetect
								className="text-center"
								onVisible={this.getJobDrafts}
							>
								<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
							</ScrollDetect>
						</If>
					</div>
					<ConfirmationModal ref={this._confirmationModal} />
				</div>
			</Fragment>
		);
	}
}

JobDrafts.propTypes = {
	user: PropTypes.object.isRequired,
	router: PropTypes.object.isRequired,
};

export default JobDrafts;
